<script lang="ts">
  /* THE FRAME — one grid, named areas, nothing positioned by hand.
   *
   * ═══ WHAT THIS REPLACES ═══
   *
   * Eight overlays positioned by hand, in three files, with constants
   * describing each other's rendered size:
   *
   *     .themebtn   { top: 78px }          .cachebtn  { top: 116px }
   *     .toolwrap   { top: 154px }         .routebox  { bottom: 96px }
   *     .scrubwrap  { bottom: 34px }       .sheet     { top: 140px }
   *     .barwrap    { left: calc((var(--gd-panel-inset) + 20px + 100vw - 150px) / 2) }
   *
   * Every one of those numbers is an unenforced claim about some other element,
   * and the history of this layout is one of them landing on another: MEASURE
   * drawn on top of CACHE, the layer bar pinned to the right of every desktop
   * for a release, the time control 177 px off the right edge in production.
   * All three were found by looking at a screenshot, because not one assertion
   * asked where anything was.
   *
   * `--gd-panel-inset` goes with them. Nothing has to be re-centred on "what
   * the panel leaves" once the panel is a grid column rather than a box on top.
   *
   * ═══ WHAT THE HOST STILL OWNS ═══
   *
   * Everything with a map in it. The shell never imports MapLibre, never asks
   * what a layer is, and takes the chart, the command bar, the tideline and the
   * credit as snippets — the same seam `configureSources` uses. What it owns is
   * the arrangement, and the arrangement is the same for every host.
   */
  import { untrack, type Snippet } from 'svelte';
  import { layout } from './layout.svelte';
  import { panels } from './panels.svelte';
  import BottomSheet from './BottomSheet.svelte';
  import Dock from './Dock.svelte';
  import Empty from '../primitives/Empty.svelte';
  import Panel from './Panel.svelte';
  import Rail from './Rail.svelte';
  import { unavailablePanel } from './panels.svelte';

  let {
    /** The map, and the controls that float ON it — the quick toggles and the
     *  zoom pair. Both act on what is under them, so both are anchored to the
     *  chart AREA rather than to the viewport, and neither can collide with
     *  anything outside it. */
    chart,
    /** Desktop and tablet only: search, the theme control, always-on state. */
    commandbar,
    /** Phone only: the quick toggles and the theme button, as one flex row. */
    toprail,
    /** The time axis. Present on every viewport — see Tideline. */
    tideline,
    /** Chart controls that belong in the phone dock rather than over the map. */
    dockTools,
    /** The credit line. A licensing requirement, not decoration. */
    fineprint,
    /** Host-owned content that must share the shell's stacking context. */
    overlay
  }: {
    chart: Snippet;
    commandbar?: Snippet;
    toprail?: Snippet;
    tideline?: Snippet;
    dockTools?: Snippet;
    fineprint?: Snippet;
    overlay?: Snippet;
  } = $props();

  const current = $derived(panels.open === null ? null : panels.get(panels.open));
  const unavailable = $derived(unavailablePanel(current));

  /* Leaving the phone layout drops the sheet: its contents are about to be
   * rendered by the column branch instead, and two copies of the same panel on
   * screen is worse than either. Kept here rather than in the layout store
   * because it is a statement about PANELS, and the store's job is to answer
   * what shape the window is. */
  $effect(() => {
    const phone = layout.phone;
    /* This effect follows the BREAKPOINT, not the value it normalises. Reading
     * and then writing detent in the tracked part made the effect subscribe to
     * its own output. Svelte 5.35+ reschedules such effects during the same
     * flush; in the production build that could exhaust the update-depth guard
     * and abort the flush which was mounting the newly selected panel. */
    untrack(() => {
      if (!phone && panels.detent !== 'open') panels.detent = 'open';
    });
  });
</script>

<div class="shell {layout.shape}" class:landscape={layout.landscape}>
  {#if !layout.phone}
    <header class="a-bar">{@render commandbar?.()}</header>
    <div class="a-rail"><Rail /></div>
  {/if}

  <div class="a-chart">
    {@render chart()}

    <!-- ═══ THE RAIL IS A LAYOUT, AND THE SHELL OWNS ITS BOX ═══
         The chart shows through between its two ends and each end brings its
         own background, so this is `pointer-events: none` with its children
         opted back in.

         It is wrapped HERE rather than by the host because of the landscape
         rule below: the one measurement still crossing a component boundary in
         the phone layout, down from fourteen. Leaving it to the host means
         every host reimplements it, and the first one to forget gets four
         toggles wrapped into a box in the middle of the chart. -->
    {#if layout.phone && toprail}
      <div class="a-toprail">{@render toprail()}</div>
    {/if}

    <!-- ═══ THE PANEL, IN WHICHEVER CONTAINER THIS VIEWPORT HAS ═══
         Same component, same props, three shapes. The panel itself is only ever
         its body — see Panel.svelte on what that retires. -->
    {#if current}
      {@const Content = current.content}
      {#if layout.phone}
        <BottomSheet
          bind:detent={panels.detent}
          label={current.title}
          onclose={() => panels.close(current.id)}
        >
          <Panel
            variant="sheet"
            title={current.title}
            peek={!unavailable && current.peek ? peekOf : undefined}
            foot={!unavailable && current.foot ? footOf : undefined}
            onclose={() => panels.close(current.id)}
          >
            {#if unavailable}
              <Empty
                title={unavailable.title}
                body={unavailable.body}
                action={unavailable.action}
              />
            {:else}
              <Content />
            {/if}
          </Panel>
        </BottomSheet>
      {:else if layout.tablet}
        <!-- A drawer, because at 1024 a docked 360 px column plus a 56 px rail
             leaves 608 px of chart and the chart stops being the interface. -->
        <aside class="panelcol drawer">
          <Panel
            title={current.title}
            foot={!unavailable && current.foot ? footOf : undefined}
            onclose={() => panels.close(current.id)}
          >
            {#if unavailable}
              <Empty
                title={unavailable.title}
                body={unavailable.body}
                action={unavailable.action}
              />
            {:else}
              <Content />
            {/if}
          </Panel>
        </aside>
      {/if}
    {/if}
  </div>

  <!-- ═══ DOCKED, NOT FLOATING ═══
       A panel that overlays the chart has to be dismissed to see what changing
       it did, which breaks the feedback loop a layers panel exists for. Docked,
       the chart simply gets narrower and stays visible. -->
  {#if layout.desktop && current}
    {@const Content = current.content}
    <aside class="a-panel panelcol">
      <Panel
        title={current.title}
        foot={!unavailable && current.foot ? footOf : undefined}
        onclose={() => panels.close(current.id)}
      >
        {#if unavailable}
          <Empty
            title={unavailable.title}
            body={unavailable.body}
            action={unavailable.action}
          />
        {:else}
          <Content />
        {/if}
      </Panel>
    </aside>
  {/if}

  {#if tideline}
    <div class="a-tide">{@render tideline()}</div>
  {/if}

  {#if layout.phone}
    <div class="a-dock">
      <Dock tools={dockTools} {fineprint} />
    </div>
  {/if}

  <!-- The overlay must live inside `.shell`'s stacking context so host-owned
       z-index ordering against notices and panels holds. -->
  {@render overlay?.()}
</div>

<!-- Snippets rather than inline markup because all three containers render the
     same two, and a snippet defined once cannot drift between them. -->
{#snippet peekOf()}{current?.peek?.() ?? ''}{/snippet}

{#snippet footOf()}
  {#if current?.foot}
    {@const Foot = current.foot}
    <Foot />
  {/if}
{/snippet}

<style>
  .shell {
    position: fixed;
    inset: 0;
    display: grid;
    background: var(--bg);
    color: var(--ink);
    font-family: var(--sans);
    overflow: hidden;
  }

  /* ─── DESKTOP ─────────────────────────────────────────────────────────── */
  .shell.desktop {
    grid-template-columns: var(--w-rail) 1fr auto;
    grid-template-rows: var(--h-commandbar) 1fr auto;
    grid-template-areas:
      'bar   bar   bar'
      'rail  chart panel'
      'tide  tide  tide';
  }

  /* ─── TABLET ──────────────────────────────────────────────────────────────
     The rail stays — it is 56 px and it is how you reach anything. The panel
     becomes an overlay inside the chart area. */
  .shell.tablet {
    grid-template-columns: var(--w-rail) 1fr;
    grid-template-rows: var(--h-commandbar) 1fr auto;
    grid-template-areas:
      'bar   bar'
      'rail  chart'
      'tide  tide';
  }

  /* ─── PHONE ───────────────────────────────────────────────────────────────
     Three objects and nothing else floats: a top rail, the tideline, a dock.
     The sheet rests on the dock. */
  .shell.phone {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto auto;
    grid-template-areas:
      'chart'
      'tide'
      'dock';
  }
  /* In landscape the sheet is a full-height column inside the chart area, so
     the tideline would be squeezed against a 393 px screen for nothing. The
     dock's time slot still carries the reading. */
  .shell.phone.landscape .a-tide {
    display: none;
  }

  .a-bar {
    grid-area: bar;
    background: var(--s-1);
    border-bottom: 1px solid var(--line);
    min-width: 0;
  }
  .a-rail {
    grid-area: rail;
    display: flex;
    min-height: 0;
  }
  .a-rail > :global(*) {
    flex: 1;
  }
  .a-chart {
    grid-area: chart;
    position: relative;
    min-width: 0;
    min-height: 0;
  }

  .a-toprail {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 5;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--sp-4);
    pointer-events: none;
    padding: calc(var(--sp-2) + env(safe-area-inset-top))
      calc(var(--sp-5) + env(safe-area-inset-right)) 0
      calc(var(--sp-5) + env(safe-area-inset-left));
  }
  .a-toprail > :global(*) {
    pointer-events: auto;
  }
  /* ═══ IN LANDSCAPE THE RAIL STOPS AT THE SHEET ═══
   *
   * A phone on its side is 852 x 393 and an open sheet is a column down the
   * right edge, so the rail across the top has to stop short of it — otherwise
   * the toggles get whatever is left and wrap 2 + 2 into a box in the middle of
   * the chart. Measured on a 734 px screen: 404 px left over, one row with room
   * to spare.
   *
   * The width is published by `BottomSheet`, which is the element it describes,
   * rather than guessed at by the one reading it. That is the difference
   * between this constant and the fourteen that were deleted. */
  .shell.landscape .a-toprail {
    right: var(--gd-sheet-w, 0px);
  }
  .a-panel {
    grid-area: panel;
  }
  .a-tide {
    grid-area: tide;
    min-width: 0;
  }
  .a-dock {
    grid-area: dock;
    display: flex;
    flex-direction: column;
  }
  .a-dock > :global(*) {
    flex: 1;
  }

  .panelcol {
    width: var(--w-panel);
    display: flex;
    flex-direction: column;
    min-height: 0;
    background: var(--s-1);
    border-left: 1px solid var(--line);
  }
  .panelcol.drawer {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 6;
    width: min(var(--w-panel), 46vw);
    box-shadow: var(--el-3);
    animation: drawer var(--dur-base) var(--ease-out);
  }
  @keyframes drawer {
    from {
      transform: translateX(100%);
    }
    to {
      transform: none;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .panelcol.drawer {
      animation: none;
    }
  }
</style>
